import React from "react"
import { Box, Typography } from "@mui/material"
import {
  SECTION_LABEL_SX,
  LegendSwatch,
} from "@/features/voyagePlanner/wizard/styles"
import { PANEL_SCROLLBAR_SX } from "@/theme/panelStyles"

const heatColor = (value) => {
  if (value >= 100) return "#e6f2ea"
  if (value >= 85) return "#fff6e0"
  if (value >= 50) return "#ffe6c7"
  return "#ffd6d1"
}

// Data columns fill the available width (minmax fills the container) but never
// shrink below a floor — below that the table scrolls horizontally instead of
// compressing, keeping the sticky first column and header row aligned.
const ANGLE_COL_WIDTH = 44
const DATA_COL_MIN_WIDTH = 56

function HeatmapMatrix({ title, unit, matrix }) {
  const angles = matrix?.speedprofile_rows_angles ?? []
  const strengths = matrix?.speedprofile_columns_strengths ?? []
  const values = matrix?.speedprofile_percent_speeds ?? []
  const gridTemplate = `${ANGLE_COL_WIDTH}px repeat(${strengths.length}, minmax(${DATA_COL_MIN_WIDTH}px, 1fr))`

  return (
    <Box
      sx={{
        border: "1px solid #e2e8ee",
        borderRadius: "8px",
        overflow: "auto",
        maxHeight: 240,
        mb: 1.5,
        ...PANEL_SCROLLBAR_SX,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridTemplate,
          fontSize: "10.5px",
          textAlign: "center",
          minWidth: "max-content",
        }}
      >
        {/* Corner: sticky on both axes, above the header row and label column. */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            left: 0,
            zIndex: 3,
            bgcolor: "#004f7d",
            color: "#fff",
            fontWeight: 700,
            py: 0.6,
            pl: 1,
            textAlign: "left",
          }}
        >
          {title}
        </Box>
        {strengths.map((strength) => (
          <Box
            key={strength}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              bgcolor: "#eef3f7",
              color: "#5b7f9c",
              py: 0.6,
            }}
          >
            {strength} {unit}
          </Box>
        ))}
        {angles.map((angle, rowIndex) => (
          <React.Fragment key={angle}>
            <Box
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 1,
                bgcolor: "#004f7d",
                color: "#fff",
                py: 0.5,
                pl: 1,
                textAlign: "left",
              }}
            >
              {angle}°
            </Box>
            {(values[rowIndex] ?? []).map((value, colIndex) => (
              <Box
                key={colIndex}
                sx={{ py: 0.5, color: "#004f7d", bgcolor: heatColor(value) }}
              >
                {value}
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  )
}

// Read-only view of the speed profile matrices; edited via the profiles
// flow, not here.
function SpeedProfileTab({ speedProfileData }) {
  const windMatrix = (speedProfileData ?? []).find((m) => m?.name === "wind")
  const waveMatrix = (speedProfileData ?? []).find((m) => m?.name === "wave")

  return (
    <>
      <Typography sx={{ fontSize: "10.5px", color: "#8a98a6", my: 1.25 }}>
        Speed impact in % (100 % = base speed in calm seas; 0° = head-on,
        180° = from astern)
      </Typography>
      {windMatrix && (
        <>
          <Typography sx={SECTION_LABEL_SX}>Wind</Typography>
          <HeatmapMatrix title="WIND" unit="bft" matrix={windMatrix} />
        </>
      )}
      {waveMatrix && (
        <>
          <Typography sx={SECTION_LABEL_SX}>Wave</Typography>
          <HeatmapMatrix title="WAVE" unit="m" matrix={waveMatrix} />
        </>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          fontSize: "9.5px",
          color: "#8a98a6",
        }}
      >
        <LegendSwatch color="#e6f2ea" label="≥ 100 %" />
        <LegendSwatch color="#fff6e0" label="85–99 %" />
        <LegendSwatch color="#ffe6c7" label="50–84 %" />
        <LegendSwatch color="#ffd6d1" label="< 50 %" />
      </Box>
    </>
  )
}

export default React.memo(SpeedProfileTab)
